import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronDown,
  ClipboardList,
  GripVertical,
  Info,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { FeatureLimitHint } from '../../../components/FeatureLimitHint';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { useFeatureLimits } from '../../../hooks/useFeatureLimits';
import { CHECKLIST_QUESTIONS_MAX } from '../../../lib/instanceFeatureLimits';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import type {
  ChecklistDetailRow,
  ChecklistListRow,
  ChecklistQuestionRow,
  ChecklistQuestionType,
} from '../types';
import {
  CHECKLIST_ANSWER_QUESTION_TYPES,
  CHECKLIST_QUESTION_TYPE_LABELS,
  CHECKLIST_SHORT_TEXT_MAX_LENGTH,
  checklistQuestionTypeIcon,
  createEmptyChecklistQuestion,
  defaultChecklistQuestionConfig,
  isChecklistDisplayOnlyType,
} from '../utils/checklistQuestionTypes';
import { ChecklistTableColumnsEditor } from '../components/ChecklistTableColumnsEditor';
import { checklistTableConfigError, normalizeChecklistTableConfig } from '../../leads/utils/checklistTable';
import {
  SETTINGS_BTN_DANGER,
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';
import { formatChecklistEditedRelative } from '../../../utils/checklistSelectOptions';
import { ActivityIcon } from '../../../utils/activityIcons';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { WORK_REPORT_APPENDIX_IMAGE_MAX_COUNT } from '../../leads/utils/workReportField';
import { parseChecklistVisibleIf } from '../../leads/utils/checklistVisibility';
import {
  checklistIdFromSettingsPath,
  checklistSettingsPath,
} from '../../../utils/settingsRoutes';

type EditorQuestion = ChecklistQuestionRow & { clientKey: string };

type ConfirmState = {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
};

function toEditorQuestions(questions: ChecklistQuestionRow[]): EditorQuestion[] {
  return questions.map((q) => ({
    ...q,
    clientKey: q.id || `q_${Math.random().toString(36).slice(2, 9)}`,
  }));
}

/** Exclusive end index of a section block (section + following items until next section). */
function sectionBlockEnd(questions: EditorQuestion[], sectionIdx: number): number {
  let end = sectionIdx + 1;
  while (end < questions.length && questions[end]?.question_type !== 'section') end += 1;
  return end;
}

function validSectionInsertIndexes(questions: EditorQuestion[]): number[] {
  const indexes = new Set<number>([0, questions.length]);
  for (let i = 0; i < questions.length; i++) {
    if (questions[i]?.question_type === 'section') indexes.add(i);
  }
  return [...indexes].sort((a, b) => a - b);
}

function snapToNearestIndex(indexes: number[], raw: number): number {
  if (indexes.length === 0) return raw;
  let best = indexes[0]!;
  let bestDist = Math.abs(raw - best);
  for (const i of indexes) {
    const d = Math.abs(raw - i);
    if (d < bestDist) {
      best = i;
      bestDist = d;
    }
  }
  return best;
}

/** True when insertIndex sits strictly among another section's questions (not on a section boundary). */
function isInsertInsideForeignSection(
  questions: EditorQuestion[],
  insertIndex: number,
  draggedSectionStart: number,
): boolean {
  for (let i = 0; i < questions.length; i++) {
    if (questions[i]?.question_type !== 'section') continue;
    if (i === draggedSectionStart) continue;
    const end = sectionBlockEnd(questions, i);
    // After header, before block end (= next section / list end)
    if (insertIndex > i && insertIndex < end) return true;
  }
  return false;
}

function validateEditor(
  name: string,
  activityTypeKey: string,
  questions: EditorQuestion[],
): {
  name?: string;
  type?: string;
  questions: Record<string, { text?: boolean; options?: boolean; columns?: boolean }>;
} {
  const errors: {
    name?: string;
    type?: string;
    questions: Record<string, { text?: boolean; options?: boolean; columns?: boolean }>;
  } = { questions: {} };
  if (!name.trim()) errors.name = 'Bitte geben Sie einen Namen ein.';
  if (!activityTypeKey.trim()) errors.type = 'Bitte einen Aktivitätstyp wählen.';
  for (const q of questions) {
    const qErr: { text?: boolean; options?: boolean; columns?: boolean } = {};
    if (!q.text.trim()) qErr.text = true;
    if (q.question_type === 'single_choice' || q.question_type === 'multiple_choice') {
      const opts = Array.isArray(q.config?.options)
        ? (q.config.options as string[]).map((o) => String(o).trim()).filter(Boolean)
        : [];
      if (opts.length < 2) qErr.options = true;
    }
    if (q.question_type === 'table' && checklistTableConfigError(normalizeChecklistTableConfig(q.config))) {
      qErr.columns = true;
    }
    if (qErr.text || qErr.options || qErr.columns) errors.questions[q.clientKey] = qErr;
  }
  return errors;
}

function RequiredToggle({
  required,
  onToggle,
}: {
  required: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={required}
      title={required ? 'Als optional markieren' : 'Als Pflichtfrage markieren'}
      className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-[12.5px] font-semibold transition-colors ${
        required
          ? 'border-blue-200 bg-blue-50 text-blue-800'
          : 'border-app-border bg-app-surface text-app-text-subtle hover:border-app-border-strong hover:bg-app-hover'
      }`}
    >
      <span
        className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
          required ? 'border-pipedrive-blue bg-pipedrive-blue text-white' : 'border-app-border-strong bg-app-surface'
        }`}
        aria-hidden
      >
        {required ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : null}
      </span>
      Pflichtfrage
    </button>
  );
}

function OptionalConfigToggle({
  open,
  onToggle,
  label,
  hasValue,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
  hasValue: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex items-center gap-1 text-xs font-semibold text-app-text-subtle hover:text-pipedrive-blue"
    >
      <ChevronDown
        className={`h-3.5 w-3.5 transition-transform ${open || hasValue ? 'rotate-0' : '-rotate-90'}`}
      />
      {hasValue && !open ? `${label} gesetzt` : label}
    </button>
  );
}

function DropInsertStripe({
  active,
  indented,
  onDragOver,
  onDrop,
}: {
  active: boolean;
  indented?: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  return (
    <div
      className={`${indented ? 'ml-5' : ''}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
      aria-hidden
    >
      <div
        className={`my-0.5 h-2 rounded transition-colors ${
          active
            ? 'bg-pipedrive-blue/30'
            : 'border border-dashed border-app-border bg-transparent'
        }`}
      />
    </div>
  );
}

function DragHandle({
  onDragStart,
  onDragEnd,
}: {
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}) {
  return (
    <span
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        onDragStart(e);
      }}
      onDragEnd={(e) => {
        e.stopPropagation();
        onDragEnd();
      }}
      title="Ziehen zum Sortieren"
      className="inline-flex cursor-grab touch-none text-slate-300 active:cursor-grabbing"
    >
      <GripVertical className="h-4 w-4" />
    </span>
  );
}

/** "PDF-Größe: Normal | Ganze Seite" for photo questions (`config.photo_layout`). */
function PhotoLayoutSwitch({ full, onChange }: { full: boolean; onChange: (full: boolean) => void }) {
  const option = (value: boolean, label: string) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      aria-pressed={full === value}
      className={`h-7 px-2.5 text-[12px] font-semibold transition-colors ${
        full === value
          ? 'bg-blue-50 text-blue-800'
          : 'bg-app-surface text-app-text-subtle hover:bg-app-hover'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex items-center gap-1.5" title="Wie groß die Fotos dieser Frage im Protokoll-PDF erscheinen">
      <span className="text-xs font-medium text-slate-400">PDF-Größe</span>
      <div className="inline-flex overflow-hidden rounded-lg border border-app-border divide-x divide-app-border">
        {option(false, 'Normal')}
        {option(true, 'Ganze Seite')}
      </div>
    </div>
  );
}

/** Single-choice question placed before a section; a section condition can refer to it. */
type SectionConditionSource = { id: string; label: string; options: string[] };

const ALWAYS_SHOW_SECTION = '__always__';

/** "Nur anzeigen, wenn …" for a section: pick an earlier single-choice question and one option. */
function SectionConditionEditor({
  config,
  sources,
  onConfigChange,
}: {
  config: Record<string, unknown> | undefined;
  sources: SectionConditionSource[];
  onConfigChange: (patch: Record<string, unknown>) => void;
}) {
  const condition = parseChecklistVisibleIf(config);
  const source = condition ? sources.find((src) => src.id === condition.question_id) : undefined;
  const invalid = Boolean(condition && (!source || !source.options.includes(condition.equals)));

  const questionOptions = [
    { value: ALWAYS_SHOW_SECTION, label: 'Immer anzeigen' },
    ...sources.map((src) => ({ value: src.id, label: `Nur wenn „${src.label}“` })),
  ];
  const answerOptions = (source?.options ?? []).map((opt) => ({ value: opt, label: opt }));

  return (
    <div className="mt-2 ml-6 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-slate-400">Anzeigen:</span>
        <AppSelectDropdown
          disabled={!condition && sources.length === 0}
          value={condition && source ? condition.question_id : ALWAYS_SHOW_SECTION}
          onChange={(value) => {
            if (value === ALWAYS_SHOW_SECTION) {
              onConfigChange({ visible_if: undefined });
              return;
            }
            const next = sources.find((src) => src.id === value);
            onConfigChange({ visible_if: { question_id: value, equals: next?.options[0] ?? '' } });
          }}
          options={questionOptions}
          className="min-w-[200px] max-w-full"
          triggerClassName={`${SETTINGS_SELECT_TRIGGER_CLASS} !text-[13px]`}
          chevronClassName="!text-slate-400"
        />
        {condition && source ? (
          <>
            <span className="text-xs font-medium text-slate-400">ist</span>
            <AppSelectDropdown
              value={source.options.includes(condition.equals) ? condition.equals : ''}
              onChange={(value) => onConfigChange({ visible_if: { question_id: source.id, equals: value } })}
              options={answerOptions}
              placeholder="Antwort wählen"
              className="min-w-[140px]"
              triggerClassName={`${SETTINGS_SELECT_TRIGGER_CLASS} !text-[13px]`}
              chevronClassName="!text-slate-400"
            />
          </>
        ) : null}
        {!condition && sources.length === 0 ? (
          <span
            className="inline-flex items-center gap-1 text-xs text-slate-400"
            title="Ein Abschnitt kann nur abhängig von einer Einfachauswahl-Frage angezeigt werden, die vor ihm steht."
          >
            <Info className="h-3.5 w-3.5 shrink-0" />
            Bedingung braucht eine Einfachauswahl-Frage davor
          </span>
        ) : null}
      </div>
      {invalid ? (
        <p className="flex items-center gap-1.5 text-xs font-medium text-red-600">
          <AlertCircle className="h-3.5 w-3.5" />
          Die Bedingung passt nicht mehr (Frage oder Antwort geändert). Bitte neu wählen oder „Immer anzeigen“.
        </p>
      ) : null}
    </div>
  );
}

function ChecklistQuestionEditorRow({
  question,
  displayIndex,
  indented,
  isDragging,
  errors,
  onTextChange,
  onTypeChange,
  onToggleRequired,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onConfigChange,
  onAddOption,
  onRemoveOption,
  onOptionChange,
  conditionSources,
}: {
  question: EditorQuestion;
  displayIndex: number | null;
  indented: boolean;
  isDragging: boolean;
  errors?: { text?: boolean; options?: boolean; columns?: boolean };
  onTextChange: (value: string) => void;
  onTypeChange: (type: ChecklistQuestionType) => void;
  onToggleRequired: () => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onConfigChange: (patch: Record<string, unknown>) => void;
  onAddOption: () => void;
  onRemoveOption: (optionIndex: number) => void;
  onOptionChange: (optionIndex: number, value: string) => void;
  /** Earlier single-choice questions (sections only). */
  conditionSources: SectionConditionSource[];
}) {
  const isSection = question.question_type === 'section';
  const isInfo = question.question_type === 'info';
  const placeholderValue = String(question.config?.placeholder ?? '');
  const hintValue = String(question.config?.hint ?? '');
  const [showPlaceholder, setShowPlaceholder] = React.useState(Boolean(placeholderValue));
  const [showHint, setShowHint] = React.useState(Boolean(hintValue));

  React.useEffect(() => {
    if (placeholderValue) setShowPlaceholder(true);
  }, [placeholderValue]);
  React.useEffect(() => {
    if (hintValue) setShowHint(true);
  }, [hintValue]);

  const typeOptions = CHECKLIST_ANSWER_QUESTION_TYPES.map((t) => {
    const Icon = checklistQuestionTypeIcon(t);
    return {
      value: t,
      label: CHECKLIST_QUESTION_TYPE_LABELS[t],
      icon: <Icon className="h-4 w-4 text-pipedrive-blue" />,
    };
  });

  const options = Array.isArray(question.config?.options)
    ? (question.config.options as string[])
    : ['', ''];

  const deleteBtnClass =
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 focus-visible:opacity-100';

  if (isSection) {
    return (
      <div
        className="mt-1"
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <div
          className={`group flex items-center gap-2 border-b-2 border-slate-700/80 pb-2.5 pt-1 transition-opacity ${
            isDragging ? 'opacity-45' : ''
          }`}
        >
          <DragHandle onDragStart={onDragStart} onDragEnd={onDragEnd} />
          <span className={`shrink-0 ${SETTINGS_FIELD_LABEL}`}>
            Abschnitt
          </span>
          <input
            type="text"
            value={question.text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="Abschnittstitel …"
            className={`min-w-0 flex-1 border-0 bg-transparent px-1 py-1 text-[15px] font-semibold text-app-text outline-none placeholder:font-medium placeholder:text-slate-400 ${
              errors?.text ? 'text-red-700' : ''
            }`}
          />
          <button type="button" onClick={onDelete} title="Abschnitt löschen" className={deleteBtnClass}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        {errors?.text ? (
          <p className="mt-1.5 ml-6 flex items-center gap-1.5 text-xs font-medium text-red-600">
            <AlertCircle className="h-3.5 w-3.5" />
            Bitte einen Abschnittstitel eingeben.
          </p>
        ) : null}
        <SectionConditionEditor
          config={question.config}
          sources={conditionSources}
          onConfigChange={onConfigChange}
        />
      </div>
    );
  }

  if (isInfo) {
    return (
      <div
        onDragOver={onDragOver}
        onDrop={onDrop}
        className={`group rounded-xl border border-app-border/80 bg-app-surface-muted/60 px-3.5 py-3 transition-[opacity,border-color] hover:border-app-border-strong ${
          indented ? 'ml-5 border-l-[3px] border-l-app-border-strong' : ''
        } ${isDragging ? 'opacity-45' : ''}`}
      >
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-1.5">
          <div className="flex items-center gap-1.5">
            <DragHandle onDragStart={onDragStart} onDragEnd={onDragEnd} />
            <span className="flex min-w-[14px] items-center justify-center">
              <Info className="h-3.5 w-3.5 text-slate-400" />
            </span>
          </div>
          <input
            type="text"
            value={question.text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="Hinweistext für Monteure …"
            className={`${SETTINGS_INPUT_CLASS} font-medium text-app-text-muted ${
              errors?.text ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''
            }`}
          />
          <button type="button" onClick={onDelete} title="Hinweis löschen" className={deleteBtnClass}>
            <Trash2 className="h-4 w-4" />
          </button>
          {errors?.text ? (
            <p className="col-start-2 flex items-center gap-1.5 text-xs font-medium text-red-600">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              Bitte einen Hinweistext eingeben.
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`group rounded-xl border border-app-border bg-app-surface px-3.5 py-3 transition-[opacity,border-color] hover:border-app-border-strong ${
        indented ? 'ml-5 border-l-[3px] border-l-app-border-strong' : ''
      } ${isDragging ? 'opacity-45' : ''}`}
    >
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto_auto] items-center gap-x-2.5 gap-y-1.5">
        <div className="flex items-center gap-1.5">
          <DragHandle onDragStart={onDragStart} onDragEnd={onDragEnd} />
          <span className="min-w-[14px] text-center text-[12px] font-semibold text-slate-400">
            {displayIndex}
          </span>
        </div>

        <input
          type="text"
          value={question.text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder="Frage eingeben …"
          className={`${SETTINGS_INPUT_CLASS} font-medium ${
            errors?.text ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''
          }`}
        />

        <div className="w-[158px]">
          <AppSelectDropdown
            value={question.question_type}
            onChange={(v) => onTypeChange(v as ChecklistQuestionType)}
            options={typeOptions}
            triggerClassName={`${SETTINGS_SELECT_TRIGGER_CLASS} !bg-app-surface-muted !text-[13px] !font-medium !text-app-text-muted`}
            chevronClassName="!text-slate-400"
          />
        </div>

        <RequiredToggle required={question.required} onToggle={onToggleRequired} />

        <button
          type="button"
          onClick={onDelete}
          title="Frage löschen"
          className={deleteBtnClass}
        >
          <Trash2 className="h-4 w-4" />
        </button>

        {errors?.text ? (
          <p className="col-start-2 flex items-center gap-1.5 text-xs font-medium text-red-600">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            Bitte einen Fragetext eingeben.
          </p>
        ) : null}

        {question.question_type !== 'date' ? (
        <div className="col-start-2 space-y-2">
          {question.question_type === 'yes_no' ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-md border border-app-border bg-app-surface-muted px-2.5 py-1 text-xs font-medium text-app-text-subtle">
                Ja
              </span>
              <span className="inline-flex items-center rounded-md border border-app-border bg-app-surface-muted px-2.5 py-1 text-xs font-medium text-app-text-subtle">
                Nein
              </span>
            </div>
          ) : null}

          {question.question_type === 'text' || question.question_type === 'short_text' ? (
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-3">
                <OptionalConfigToggle
                  open={showPlaceholder}
                  onToggle={() => setShowPlaceholder((v) => !v)}
                  label="Platzhalter"
                  hasValue={Boolean(placeholderValue.trim())}
                />
                {question.question_type === 'short_text' ? (
                  <span className="text-xs text-slate-400">max. {CHECKLIST_SHORT_TEXT_MAX_LENGTH} Zeichen</span>
                ) : null}
              </div>
              {showPlaceholder ? (
                <input
                  type="text"
                  value={placeholderValue}
                  onChange={(e) => onConfigChange({ placeholder: e.target.value })}
                  placeholder={
                    question.question_type === 'short_text' ? 'z. B. SN-2024-…' : 'z. B. Bemerkungen …'
                  }
                  className={`${SETTINGS_INPUT_CLASS} w-full`}
                />
              ) : null}
            </div>
          ) : null}

          {question.question_type === 'number' ? (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5">
                <label className="text-xs font-medium text-slate-400">Min.</label>
                <input
                  type="text"
                  value={String(question.config?.min ?? '')}
                  onChange={(e) => onConfigChange({ min: e.target.value })}
                  placeholder="—"
                  className={`${SETTINGS_INPUT_CLASS} w-[64px]`}
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs font-medium text-slate-400">Max.</label>
                <input
                  type="text"
                  value={String(question.config?.max ?? '')}
                  onChange={(e) => onConfigChange({ max: e.target.value })}
                  placeholder="—"
                  className={`${SETTINGS_INPUT_CLASS} w-[64px]`}
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs font-medium text-slate-400">Einheit</label>
                <input
                  type="text"
                  value={String(question.config?.unit ?? '')}
                  onChange={(e) => onConfigChange({ unit: e.target.value })}
                  placeholder="z. B. kW"
                  className={`${SETTINGS_INPUT_CLASS} w-[80px]`}
                />
              </div>
            </div>
          ) : null}

          {question.question_type === 'photo' || question.question_type === 'photos' ? (
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {question.question_type === 'photos' ? (
                <div className="flex items-center gap-1.5">
                  <label className="text-xs font-medium text-slate-400">Max. Bilder</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={
                      question.config?.max_images != null && String(question.config.max_images).trim() !== ''
                        ? String(question.config.max_images)
                        : ''
                    }
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^\d]/g, '');
                      if (!raw) {
                        onConfigChange({ max_images: '' });
                        return;
                      }
                      const n = Math.min(Math.max(Number(raw), 1), WORK_REPORT_APPENDIX_IMAGE_MAX_COUNT);
                      onConfigChange({ max_images: n });
                    }}
                    placeholder="—"
                    className={`${SETTINGS_INPUT_CLASS} w-[64px]`}
                  />
                </div>
              ) : null}
                <PhotoLayoutSwitch
                  full={question.config?.photo_layout === 'full'}
                  onChange={(full) => onConfigChange({ photo_layout: full ? 'full' : undefined })}
                />
              </div>
              <OptionalConfigToggle
                open={showHint}
                onToggle={() => setShowHint((v) => !v)}
                label="Foto-Hinweis"
                hasValue={Boolean(hintValue.trim())}
              />
              {showHint ? (
                <input
                  type="text"
                  value={hintValue}
                  onChange={(e) => onConfigChange({ hint: e.target.value })}
                  placeholder="z. B. Typenschild fotografieren"
                  className={`${SETTINGS_INPUT_CLASS} w-full`}
                />
              ) : null}
            </div>
          ) : null}

          {question.question_type === 'table' ? (
            <ChecklistTableColumnsEditor
              config={question.config}
              showErrors={Boolean(errors?.columns)}
              onConfigChange={onConfigChange}
            />
          ) : null}

          {question.question_type === 'single_choice' || question.question_type === 'multiple_choice' ? (
            <div className="space-y-2">
              {options.map((opt, optIdx) => (
                <div key={`${question.clientKey}-opt-${optIdx}`} className="flex items-center gap-2">
                  <span
                    className={`h-3.5 w-3.5 shrink-0 border border-app-border-strong ${
                      question.question_type === 'single_choice' ? 'rounded-full' : 'rounded'
                    }`}
                  />
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => onOptionChange(optIdx, e.target.value)}
                    placeholder="Option …"
                    className={`${SETTINGS_INPUT_CLASS} flex-1`}
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveOption(optIdx)}
                    disabled={options.length <= 1}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={onAddOption}
                className="inline-flex items-center gap-1 text-xs font-semibold text-pipedrive-blue hover:underline"
              >
                <Plus className="h-3.5 w-3.5" />
                Option hinzufügen
              </button>
              {errors?.options ? (
                <p className="flex items-center gap-1.5 text-xs font-medium text-red-600">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Mindestens 2 Optionen erforderlich.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
        ) : null}
      </div>
    </div>
  );
}

export function ChecklistsSettingsSection() {
  const location = useLocation();
  const navigate = useNavigate();
  const { instanceId = 'default' } = useParams<{ instanceId: string }>();
  const plainPath = React.useMemo(
    () => location.pathname.replace(/^\/i\/[^/]+/, ''),
    [location.pathname],
  );
  const routeChecklistId = checklistIdFromSettingsPath(plainPath);
  const mode: 'list' | 'editor' = routeChecklistId ? 'editor' : 'list';
  const checklistPath = React.useCallback(
    (checklistId?: string) =>
      `/i/${encodeURIComponent(instanceId)}${checklistSettingsPath(checklistId)}`,
    [instanceId],
  );
  const [checklists, setChecklists] = React.useState<ChecklistListRow[]>([]);
  const [activityTypes, setActivityTypes] = React.useState<
    { id: string; label: string; icon: string }[]
  >([]);
  const [loaded, setLoaded] = React.useState(false);
  const { usage, loaded: limitsReady } = useFeatureLimits();
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [name, setName] = React.useState('');
  const [activityTypeKey, setActivityTypeKey] = React.useState('');
  const [questions, setQuestions] = React.useState<EditorQuestion[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showValidation, setShowValidation] = React.useState(false);
  const [confirm, setConfirm] = React.useState<ConfirmState | null>(null);
  const [dragKey, setDragKey] = React.useState<string | null>(null);
  const [dropInsertIndex, setDropInsertIndex] = React.useState<number | null>(null);
  const dragKeyRef = React.useRef<string | null>(null);
  const dropInsertIndexRef = React.useRef<number | null>(null);
  const routeChecklistIdRef = React.useRef(routeChecklistId);
  routeChecklistIdRef.current = routeChecklistId;
  const editingIdRef = React.useRef(editingId);
  editingIdRef.current = editingId;

  const loadList = React.useCallback(async () => {
    const res = await fetch('/api/settings/checklists');
    if (res.ok) {
      const data = (await res.json()) as ChecklistListRow[];
      setChecklists(Array.isArray(data) ? data : []);
    }
    setLoaded(true);
  }, []);

  React.useEffect(() => {
    void loadList();
    fetch('/api/settings/activity-types')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const rows = Array.isArray(data) ? data : [];
        setActivityTypes(
          rows.map((t: { id: string; label: string; icon?: string }) => ({
            id: String(t.id),
            label: String(t.label),
            icon: String(t.icon ?? 'task'),
          })),
        );
      })
      .catch(() => setActivityTypes([]));
  }, [loadList]);

  const activityTypeOptions = React.useMemo(
    () =>
      activityTypes.map((t) => ({
        value: t.id,
        label: t.label,
        icon: <ActivityIcon iconId={t.icon} className="h-4 w-4 text-pipedrive-blue" />,
      })),
    [activityTypes],
  );

  const validation = React.useMemo(
    () => validateEditor(name, activityTypeKey, questions),
    [name, activityTypeKey, questions],
  );

  const displayIndexes = React.useMemo(() => {
    const map = new Map<string, number | null>();
    let n = 0;
    for (const q of questions) {
      if (q.question_type === 'section') {
        n = 0;
        map.set(q.clientKey, null);
      } else if (isChecklistDisplayOnlyType(q.question_type)) {
        map.set(q.clientKey, null);
      } else {
        n += 1;
        map.set(q.clientKey, n);
      }
    }
    return map;
  }, [questions]);

  const indentFlags = React.useMemo(() => {
    const map = new Map<string, boolean>();
    let underSection = false;
    for (const q of questions) {
      if (q.question_type === 'section') {
        underSection = true;
        map.set(q.clientKey, false);
      } else {
        map.set(q.clientKey, underSection);
      }
    }
    return map;
  }, [questions]);

  const editorHasErrors = Boolean(
    validation.name || validation.type || Object.keys(validation.questions).length > 0,
  );

  const clearEditorFields = React.useCallback(() => {
    setEditingId(null);
    setName('');
    setActivityTypeKey('');
    setQuestions([]);
    setShowValidation(false);
    setError(null);
    setSaved(false);
  }, []);

  const openCreate = () => {
    if (checklists.length >= usage('checklists').max) return;
    navigate(checklistPath('new'));
  };

  // URL is source of truth for which checklist is open. Cancel in-flight loads on route change
  // so a slow response cannot overwrite another checklist or the create form.
  React.useEffect(() => {
    if (!routeChecklistId) {
      clearEditorFields();
      return;
    }

    if (routeChecklistId === 'new') {
      clearEditorFields();
      return;
    }

    // Already showing this checklist (e.g. create → navigate after save). Skip reload so
    // "Gespeichert." is not cleared and we avoid a redundant fetch.
    if (editingIdRef.current === routeChecklistId) {
      return;
    }

    let cancelled = false;
    setError(null);
    setShowValidation(false);
    setSaved(false);

    void (async () => {
      const res = await fetch(`/api/settings/checklists/${encodeURIComponent(routeChecklistId)}`);
      if (cancelled) return;
      if (!res.ok) {
        setError('Checkliste konnte nicht geladen werden.');
        setEditingId(null);
        setName('');
        setActivityTypeKey('');
        setQuestions([]);
        return;
      }
      const detail = (await res.json()) as ChecklistDetailRow;
      if (cancelled) return;
      setEditingId(detail.id);
      setName(detail.name);
      setActivityTypeKey(detail.activity_type_key);
      setQuestions(toEditorQuestions(detail.questions ?? []));
    })();

    return () => {
      cancelled = true;
    };
  }, [clearEditorFields, routeChecklistId]);

  // Fill default activity type on create without wiping name/questions when types load later.
  React.useEffect(() => {
    if (routeChecklistId !== 'new') return;
    if (activityTypeKey) return;
    const first = activityTypes[0]?.id;
    if (first) setActivityTypeKey(first);
  }, [activityTypeKey, activityTypes, routeChecklistId]);

  const openEdit = (id: string) => {
    navigate(checklistPath(id));
  };

  const backToList = () => {
    navigate(checklistPath());
  };

  const saveChecklist = async () => {
    setShowValidation(true);
    const v = validateEditor(name, activityTypeKey, questions);
    if (v.name || v.type || Object.keys(v.questions).length > 0) return;

    // Prefer URL over editor state so a stale load cannot cause saving the wrong template.
    const saveTargetId =
      routeChecklistId && routeChecklistId !== 'new' ? routeChecklistId : null;

    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const payload = {
        name: name.trim(),
        activity_type_key: activityTypeKey,
        questions: questions.map((q, i) => ({
          id: q.id,
          text: q.text.trim(),
          question_type: q.question_type,
          required: isChecklistDisplayOnlyType(q.question_type) ? false : q.required,
          config: q.config ?? {},
          sort_order: i,
        })),
      };
      const url = saveTargetId
        ? `/api/settings/checklists/${encodeURIComponent(saveTargetId)}`
        : '/api/settings/checklists';
      const res = await fetch(url, {
        method: saveTargetId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = (await res.json()) as ChecklistDetailRow & { error?: string };
      if (!res.ok) throw new Error(data.error || 'Speichern fehlgeschlagen.');
      // Ignore response if the user navigated away while saving.
      if (
        (saveTargetId && routeChecklistIdRef.current !== saveTargetId) ||
        (!saveTargetId && routeChecklistIdRef.current !== 'new')
      ) {
        return;
      }
      setEditingId(data.id);
      editingIdRef.current = data.id;
      setName(data.name);
      setActivityTypeKey(data.activity_type_key);
      setQuestions(toEditorQuestions(data.questions ?? []));
      if (routeChecklistIdRef.current === 'new') {
        navigate(checklistPath(data.id), { replace: true });
      }
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
      await loadList();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const deleteChecklist = (row: ChecklistListRow) => {
    setConfirm({
      title: 'Checkliste löschen?',
      body: `„${row.name}" wird dauerhaft entfernt. Bereits zugeordnete Aktivitäten verlieren die Zuordnung.`,
      confirmLabel: 'Checkliste löschen',
      onConfirm: async () => {
        setConfirm(null);
        const res = await fetch(`/api/settings/checklists/${encodeURIComponent(row.id)}`, { method: 'DELETE' });
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { error?: string };
          setError(data.error || 'Löschen fehlgeschlagen.');
          return;
        }
        await loadList();
        if (routeChecklistId === row.id) backToList();
      },
    });
  };

  const deleteQuestion = (clientKey: string) => {
    const row = questions.find((q) => q.clientKey === clientKey);
    const isSection = row?.question_type === 'section';
    const isInfo = row?.question_type === 'info';
    setConfirm({
      title: isSection ? 'Abschnitt löschen?' : isInfo ? 'Hinweis löschen?' : 'Frage löschen?',
      body: isSection
        ? 'Dieser Abschnittstitel wird aus der Vorlage entfernt.'
        : isInfo
          ? 'Dieser Hinweis wird aus der Vorlage entfernt.'
          : 'Diese Frage wird aus der Vorlage entfernt. Bereits abgeschlossene Arbeitsberichte behalten die alte Antwort.',
      confirmLabel: isSection ? 'Abschnitt löschen' : isInfo ? 'Hinweis löschen' : 'Frage löschen',
      onConfirm: () => {
        setQuestions((prev) => prev.filter((q) => q.clientKey !== clientKey));
        setConfirm(null);
      },
    });
  };

  const reorderQuestionsToIndex = (fromKey: string, insertIndex: number) => {
    setQuestions((prev) => {
      const fromIdx = prev.findIndex((q) => q.clientKey === fromKey);
      if (fromIdx < 0) return prev;

      if (prev[fromIdx]?.question_type === 'section') {
        const start = fromIdx;
        const end = sectionBlockEnd(prev, fromIdx);
        const blockLen = end - start;
        const rawTarget = Math.max(0, Math.min(prev.length, insertIndex));

        // Same place as now (before/inside/right after own block)
        if (rawTarget >= start && rawTarget <= end) return prev;

        const splitForeignSection = isInsertInsideForeignSection(prev, rawTarget, start);
        const target = splitForeignSection
          ? rawTarget
          : snapToNearestIndex(validSectionInsertIndexes(prev), rawTarget);

        // After snapping, still no-op?
        if (target >= start && target <= end) return prev;

        const next = [...prev];
        if (splitForeignSection) {
          // Move only the marker into the target section. The target questions
          // below it now belong to this section; the old children stay behind.
          const [section] = next.splice(start, 1);
          if (!section) return prev;
          const adjusted = target > start ? target - 1 : target;
          next.splice(Math.max(0, Math.min(next.length, adjusted)), 0, section);
          return next;
        }

        // At a section boundary, move the complete section block.
        const block = next.splice(start, blockLen);
        const adjusted = target > start ? target - blockLen : target;
        next.splice(Math.max(0, Math.min(next.length, adjusted)), 0, ...block);
        return next;
      }

      if (insertIndex === fromIdx || insertIndex === fromIdx + 1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      if (!moved) return prev;
      const adjusted = insertIndex > fromIdx ? insertIndex - 1 : insertIndex;
      next.splice(Math.max(0, Math.min(next.length, adjusted)), 0, moved);
      return next;
    });
  };

  const updateDropIndicator = (e: React.DragEvent, rowIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const before = e.clientY < rect.top + rect.height / 2;
    let nextIndex = before ? rowIndex : rowIndex + 1;

    if (dragKey) {
      const fromIdx = questions.findIndex((q) => q.clientKey === dragKey);
      if (fromIdx >= 0 && questions[fromIdx]?.question_type === 'section') {
        const overType = questions[rowIndex]?.question_type;
        const splitForeignSection =
          overType != null &&
          overType !== 'section' &&
          isInsertInsideForeignSection(questions, nextIndex, fromIdx);

        if (overType === 'section' && rowIndex !== fromIdx) {
          // Upper/lower half of a section title means before/after its full block.
          nextIndex = before ? rowIndex : sectionBlockEnd(questions, rowIndex);
        } else if (!splitForeignSection) {
          nextIndex = snapToNearestIndex(validSectionInsertIndexes(questions), nextIndex);
        }
      }
    }

    dropInsertIndexRef.current = nextIndex;
    setDropInsertIndex(nextIndex);
  };

  const updateDropBand = (e: React.DragEvent, rawIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    let nextIndex = rawIndex;

    const key = dragKeyRef.current;
    if (key) {
      const fromIdx = questions.findIndex((q) => q.clientKey === key);
      if (fromIdx >= 0 && questions[fromIdx]?.question_type === 'section') {
        if (!isInsertInsideForeignSection(questions, nextIndex, fromIdx)) {
          nextIndex = snapToNearestIndex(validSectionInsertIndexes(questions), nextIndex);
        }
      }
    }

    dropInsertIndexRef.current = nextIndex;
    setDropInsertIndex(nextIndex);
  };

  const dropDraggedItem = (e: React.DragEvent, fallbackIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    const key =
      dragKeyRef.current ||
      e.dataTransfer.getData('text/plain') ||
      null;
    const insertAt = dropInsertIndexRef.current ?? fallbackIndex;
    if (key) reorderQuestionsToIndex(key, insertAt);
    clearDragState();
  };

  const draggedKeys = React.useMemo(() => {
    if (!dragKey) return new Set<string>();
    return new Set([dragKey]);
  }, [dragKey]);

  const draggedSectionRange = React.useMemo(() => {
    if (!dragKey) return null;
    const start = questions.findIndex((q) => q.clientKey === dragKey);
    if (start < 0 || questions[start]?.question_type !== 'section') return null;
    return { start, end: sectionBlockEnd(questions, start) };
  }, [dragKey, questions]);

  const clearDragState = () => {
    dragKeyRef.current = null;
    dropInsertIndexRef.current = null;
    setDragKey(null);
    setDropInsertIndex(null);
  };

  const checklistLimit = usage('checklists');
  const checklistsAtLimit = checklists.length >= checklistLimit.max;
  const questionsAtLimit = questions.length >= CHECKLIST_QUESTIONS_MAX;

  const appendQuestion = (type: ChecklistQuestionType) => {
    if (questions.length >= CHECKLIST_QUESTIONS_MAX) return;
    // Don't show leftover save-validation errors on a freshly added empty row.
    setShowValidation(false);
    setQuestions((prev) => [
      ...prev,
      {
        ...createEmptyChecklistQuestion(type),
        clientKey: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        sort_order: prev.length,
      },
    ]);
  };

  if (mode === 'editor') {
    return (
      <>
      <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      <SettingsCard
        title={routeChecklistId && routeChecklistId !== 'new' ? 'Checkliste' : 'Checkliste anlegen'}
        leading={
          <button
            type="button"
            onClick={backToList}
            className="rounded-lg p-1 text-slate-400 hover:bg-app-hover hover:text-app-text-muted"
            title="Zurück zur Übersicht"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        }
        actions={
          <>
            <SettingsCardSavedStatus saved={saved} />
            <button type="button" onClick={backToList} className={SETTINGS_BTN_OUTLINE}>
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void saveChecklist()}
              disabled={saving || (showValidation && editorHasErrors)}
              className={SETTINGS_BTN_PRIMARY}
            >
              {saving ? 'Speichern…' : 'Speichern'}
            </button>
          </>
        }
      >
        <div className="border-b border-app-border pb-5 mb-5">
          <div className="flex flex-wrap items-start gap-5">
            <div className="min-w-[260px] flex-[2]">
              <label className={`${SETTINGS_FIELD_LABEL} mb-1.5 block`}>Name der Checkliste</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z. B. Abnahme Heizungsanlage"
                className={`w-full rounded-[10px] border border-app-border px-3.5 py-2.5 text-xl font-semibold text-app-text outline-none focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20 ${
                  showValidation && validation.name
                    ? 'border-red-400 focus:border-red-400 focus:ring-red-200'
                    : ''
                }`}
              />
              {showValidation && validation.name ? (
                <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {validation.name}
                </p>
              ) : null}
            </div>
            <div className="min-w-[220px] flex-1">
              <label className={`${SETTINGS_FIELD_LABEL} mb-1.5 block`}>Aktivitätstyp</label>
              <AppSelectDropdown
                value={activityTypeKey}
                onChange={setActivityTypeKey}
                options={activityTypeOptions}
                placeholder="Aktivitätstyp wählen"
                className="w-full"
                triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                chevronClassName="!text-slate-400"
              />
              {showValidation && validation.type ? (
                <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {validation.type}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          <p className="text-sm text-app-text-subtle">
            Per Drag &amp; Drop sortieren. Abschnitte und Hinweise erscheinen nur als Anzeige.
          </p>

          <div
            className="space-y-0"
            onDragOver={(e) => {
              if (!dragKeyRef.current) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
          >
            {questions.map((q, idx) => {
              const activeDropBand =
                Boolean(dragKey) &&
                dropInsertIndex === idx &&
                !(
                  draggedSectionRange &&
                  idx >= draggedSectionRange.start &&
                  idx <= draggedSectionRange.end
                );
              return (
              <React.Fragment key={q.clientKey}>
                <DropInsertStripe
                  active={activeDropBand}
                  indented={indentFlags.get(q.clientKey) ?? false}
                  onDragOver={(e) => updateDropBand(e, idx)}
                  onDrop={(e) => dropDraggedItem(e, idx)}
                />
                <ChecklistQuestionEditorRow
                  question={q}
                  conditionSources={
                    q.question_type === 'section'
                      ? questions
                          .slice(0, idx)
                          .filter((prev) => prev.question_type === 'single_choice')
                          .map((prev) => ({
                            id: prev.id,
                            label: prev.text.trim() || 'Ohne Text',
                            options: (Array.isArray(prev.config?.options) ? (prev.config.options as string[]) : [])
                              .map((o) => String(o ?? '').trim())
                              .filter(Boolean),
                          }))
                      : []
                  }
                  displayIndex={displayIndexes.get(q.clientKey) ?? null}
                  indented={indentFlags.get(q.clientKey) ?? false}
                  isDragging={draggedKeys.has(q.clientKey)}
                  errors={showValidation ? validation.questions[q.clientKey] : undefined}
                  onTextChange={(value) =>
                    setQuestions((prev) =>
                      prev.map((row) => (row.clientKey === q.clientKey ? { ...row, text: value } : row)),
                    )
                  }
                  onTypeChange={(type) =>
                    setQuestions((prev) =>
                      prev.map((row) => {
                        if (row.clientKey !== q.clientKey) return row;
                        const config = defaultChecklistQuestionConfig(type);
                        return {
                          ...row,
                          question_type: type,
                          config,
                          required: isChecklistDisplayOnlyType(type) ? false : row.required,
                        };
                      }),
                    )
                  }
                  onToggleRequired={() =>
                    setQuestions((prev) =>
                      prev.map((row) =>
                        row.clientKey === q.clientKey ? { ...row, required: !row.required } : row,
                      ),
                    )
                  }
                  onDelete={() => deleteQuestion(q.clientKey)}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', q.clientKey);
                    dragKeyRef.current = q.clientKey;
                    dropInsertIndexRef.current = idx;
                    setDragKey(q.clientKey);
                    setDropInsertIndex(idx);
                  }}
                  onDragOver={(e) => updateDropIndicator(e, idx)}
                  onDrop={(e) => dropDraggedItem(e, idx)}
                  onDragEnd={() => {
                    // dragend can run before/after drop depending on browser — delay clear
                    window.setTimeout(() => {
                      if (dragKeyRef.current) clearDragState();
                    }, 0);
                  }}
                  onConfigChange={(patch) =>
                    setQuestions((prev) =>
                      prev.map((row) =>
                        row.clientKey === q.clientKey
                          ? { ...row, config: { ...row.config, ...patch } }
                          : row,
                      ),
                    )
                  }
                  onAddOption={() =>
                    setQuestions((prev) =>
                      prev.map((row) => {
                        if (row.clientKey !== q.clientKey) return row;
                        const opts = Array.isArray(row.config?.options)
                          ? [...(row.config.options as string[])]
                          : ['', ''];
                        opts.push('');
                        return { ...row, config: { ...row.config, options: opts } };
                      }),
                    )
                  }
                  onRemoveOption={(optIdx) =>
                    setQuestions((prev) =>
                      prev.map((row) => {
                        if (row.clientKey !== q.clientKey) return row;
                        const opts = Array.isArray(row.config?.options)
                          ? [...(row.config.options as string[])]
                          : [];
                        opts.splice(optIdx, 1);
                        return { ...row, config: { ...row.config, options: opts.length ? opts : [''] } };
                      }),
                    )
                  }
                  onOptionChange={(optIdx, value) =>
                    setQuestions((prev) =>
                      prev.map((row) => {
                        if (row.clientKey !== q.clientKey) return row;
                        const opts = Array.isArray(row.config?.options)
                          ? [...(row.config.options as string[])]
                          : ['', ''];
                        opts[optIdx] = value;
                        return { ...row, config: { ...row.config, options: opts } };
                      }),
                    )
                  }
                />
              </React.Fragment>
              );
            })}
            <DropInsertStripe
              active={Boolean(
                dragKey &&
                  dropInsertIndex === questions.length &&
                  !(
                    draggedSectionRange &&
                    draggedSectionRange.end === questions.length
                  ),
              )}
              onDragOver={(e) => updateDropBand(e, questions.length)}
              onDrop={(e) => dropDraggedItem(e, questions.length)}
            />
            {questions.length === 0 ? (
              <p className="rounded-lg border border-dashed border-app-border py-8 text-center text-sm text-app-text-subtle">
                Noch keine Fragen. Fügen Sie die erste Frage oder einen Abschnitt hinzu.
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => appendQuestion('text')}
              disabled={questionsAtLimit}
              className={`${SETTINGS_BTN_PRIMARY} disabled:opacity-40`}
            >
              <Plus className="h-4 w-4" />
              Frage hinzufügen
            </button>
            <button
              type="button"
              onClick={() => appendQuestion('section')}
              disabled={questionsAtLimit}
              className={`${SETTINGS_BTN_OUTLINE} disabled:opacity-40`}
            >
              <Plus className="h-4 w-4" />
              Abschnitt
            </button>
            <button
              type="button"
              onClick={() => appendQuestion('info')}
              disabled={questionsAtLimit}
              className={`${SETTINGS_BTN_OUTLINE} border-transparent text-app-text-subtle hover:text-app-text-muted disabled:opacity-40`}
            >
              <Plus className="h-4 w-4" />
              Hinweis
            </button>
          </div>
          {questionsAtLimit ? (
            <p className="pt-2 text-sm text-app-text-subtle">
              Höchstens {CHECKLIST_QUESTIONS_MAX} Fragen pro Checkliste.
            </p>
          ) : null}
        </div>
      </SettingsCard>
      </div>

        {confirm ? <ConfirmDialog confirm={confirm} onCancel={() => setConfirm(null)} /> : null}
      </>
    );
  }

  return (
    <>
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
    <SettingsCard
      title="Checklisten"
      description="Erstellen Sie Fragebögen, die Monteure bei bestimmten Aktivitätstypen ausfüllen. Ob die Antworten im Arbeitsbericht landen, als eigenes Dokument oder allein stehen, legen Sie unter Aktivitäten fest (Dokumentabschluss pro Typ)."
      actions={
        loaded && checklists.length > 0 ? (
          <>
            <FeatureLimitHint
              limitKey="checklists"
              used={checklists.length}
              max={checklistLimit.max}
              ready={limitsReady}
            />
            <button
              type="button"
              onClick={openCreate}
              disabled={checklistsAtLimit}
              className={`${SETTINGS_BTN_PRIMARY} disabled:opacity-40`}
            >
              <Plus className="h-4 w-4" />
              Checkliste anlegen
            </button>
          </>
        ) : null
      }
    >
      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      {!loaded ? (
        <InlineLoadingState label="Lade…" className="justify-start" />
      ) : checklists.length === 0 ? (
        <div className="flex flex-col items-center px-4 py-10 text-center sm:py-14">
          <ClipboardList className="mb-5 h-10 w-10 text-pipedrive-blue" />
          <h3 className="text-lg font-semibold text-app-text">Noch keine Checklisten</h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-app-text-muted">
            Erstellen Sie Fragebögen, die Monteure bei bestimmten Aktivitätstypen ausfüllen. Ob die Antworten im
            Arbeitsbericht landen, als eigenes Dokument oder allein stehen, legen Sie unter Aktivitäten fest.
          </p>
          <button
            type="button"
            onClick={openCreate}
            disabled={checklistsAtLimit}
            className={`${SETTINGS_BTN_PRIMARY} mt-6 disabled:opacity-40`}
          >
            <Plus className="h-4 w-4" />
            Checkliste anlegen
          </button>
          <FeatureLimitHint
            limitKey="checklists"
            used={checklists.length}
            max={checklistLimit.max}
            ready={limitsReady}
            className="mt-3"
          />
        </div>
      ) : (
        <>
          <ul className="space-y-2.5">
            {checklists.map((row) => (
              <li
                key={row.id}
                className="flex items-center gap-4 rounded-[10px] border border-app-border bg-app-surface-muted px-4 py-[15px] transition-colors hover:border-app-border-strong hover:shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => void openEdit(row.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="block text-[15px] font-semibold text-app-text">{row.name}</span>
                  <span className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
                    <span className="inline-flex items-center rounded-md bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                      {row.activity_type_label || row.activity_type_key}
                    </span>
                    <span className="text-[12.5px] text-slate-400">·</span>
                    <span className="text-[13px] text-app-text-subtle">
                      {row.question_count} Frage{row.question_count === 1 ? '' : 'n'}
                    </span>
                    <span className="text-[12.5px] text-slate-400">·</span>
                    <span className="text-[13px] text-slate-400">
                      Zuletzt bearbeitet {formatChecklistEditedRelative(row.updated_at)}
                    </span>
                  </span>
                </button>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => void openEdit(row.id)}
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-app-border bg-app-surface text-app-text-muted hover:border-app-border-strong hover:bg-app-hover hover:text-app-text"
                    title="Bearbeiten"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteChecklist(row)}
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-app-border bg-app-surface text-app-text-subtle hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                    title="Löschen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-4 flex items-start gap-2 text-[12.5px] text-slate-400">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Pro Aktivität kann optional eine Checkliste gewählt werden. In den Dropdowns erscheinen Vorlagen,
              deren Aktivitätstyp zur geplanten Aktivität passt.
            </span>
          </p>
        </>
      )}
    </SettingsCard>
    </div>

      {confirm ? <ConfirmDialog confirm={confirm} onCancel={() => setConfirm(null)} /> : null}
    </>
  );
}

function ConfirmDialog({
  confirm,
  onCancel,
}: {
  confirm: ConfirmState;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-lg border border-app-border bg-app-surface p-5 shadow-lg">
        <div className="flex gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-app-text">{confirm.title}</h3>
            <p className="mt-1 text-sm text-app-text-muted">{confirm.body}</p>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className={SETTINGS_BTN_OUTLINE}>
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => confirm.onConfirm()}
            className={SETTINGS_BTN_DANGER}
          >
            {confirm.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
